import { Action } from 'redux'
import { isType } from 'ts-action'
import {
  ACCEPTANCE_CRITERIA_OPTION,
  ShiftPostStep,
} from '@features/shift-post-v2/steps'
import { IRoster } from '@src/models/Roster'
import { moveItem, reorderArray } from '@src/helpers/helperFuncs'
import { IShiftPostV2State } from './shift-post-v2.types'
import {
  UpdateShiftDetailsData,
  UpdateAcceptanceCriteriaData,
  UpdateScreeningRequirementsData,
  UpdateInviteUpshiftersData,
  UpdateConfirmData,
  ConfirmStep,
  ResetShiftPostV2,
  SetShiftPostV2Loading,
  SetShiftPostV2Error,
  SetShiftPostV2InitialValues,
  GetRosters,
  RostersFetching,
  GetRostersErrors,
  MoveRoster,
  SetShiftPostV2CopyEditInitialValues,
  SetShiftPostV2CopyEditStatusForRequest,
  AddBadgesToEditInitialValues,
  AddAutoAcceptCertificateIdsToEditInitialValues,
  ReorderRosters,
  ReorderRostersByInitialIds,
  GetInvitesHistory,
  InvitesHistoryFetching,
  GetInvitesHistoryErrors,
} from './shift-post-v2.actions'

const initialState: IShiftPostV2State = {
  steps: {
    [ShiftPostStep.SHIFT_DETAILS]: {
      data: {},
      confirmed: true,
    },
    [ShiftPostStep.ACCEPTANCE_CRITERIA]: {
      data: {
        selectedOption: ACCEPTANCE_CRITERIA_OPTION.VERIFIED,
        selectedRosters: [],
      },
      confirmed: true,
    },
    [ShiftPostStep.SCREENING_REQUIREMENTS]: {
      data: {},
      confirmed: true,
    },
    [ShiftPostStep.INVITE_UPSHIFTERS]: {
      data: {},
      confirmed: true,
    },
    [ShiftPostStep.CONFIRM]: {
      data: {},
      confirmed: true,
    },
  },
  isLoading: false,
  error: null,
  initialValues: null,
  copyEditInitialValues: null,
  copyEditStatusForRequest: null,
  rosterData: {
    isFetching: false,
    error: null,
    data: [],
  },
  invitesHistoryData: {
    isFetching: false,
    error: null,
    data: null,
  },
}

export const shiftPostV2Reducer = (
  state = initialState,
  action: Action,
): IShiftPostV2State => {
  // Update specific step data actions
  if (isType(action, UpdateShiftDetailsData)) {
    return {
      ...state,
      steps: {
        ...state.steps,
        [ShiftPostStep.SHIFT_DETAILS]: {
          ...state.steps[ShiftPostStep.SHIFT_DETAILS],
          data: {
            ...state.steps[ShiftPostStep.SHIFT_DETAILS].data,
            ...action.payload,
          },
        },
      },
    }
  }

  if (isType(action, UpdateAcceptanceCriteriaData)) {
    return {
      ...state,
      steps: {
        ...state.steps,
        [ShiftPostStep.ACCEPTANCE_CRITERIA]: {
          ...state.steps[ShiftPostStep.ACCEPTANCE_CRITERIA],
          data: {
            ...state.steps[ShiftPostStep.ACCEPTANCE_CRITERIA].data,
            ...action.payload,
          },
        },
      },
    }
  }

  if (isType(action, UpdateScreeningRequirementsData)) {
    return {
      ...state,
      steps: {
        ...state.steps,
        [ShiftPostStep.SCREENING_REQUIREMENTS]: {
          ...state.steps[ShiftPostStep.SCREENING_REQUIREMENTS],
          data: {
            ...state.steps[ShiftPostStep.SCREENING_REQUIREMENTS].data,
            ...action.payload,
          },
        },
      },
    }
  }

  if (isType(action, UpdateInviteUpshiftersData)) {
    return {
      ...state,
      steps: {
        ...state.steps,
        [ShiftPostStep.INVITE_UPSHIFTERS]: {
          ...state.steps[ShiftPostStep.INVITE_UPSHIFTERS],
          data: {
            ...state.steps[ShiftPostStep.INVITE_UPSHIFTERS].data,
            ...action.payload,
          },
        },
      },
    }
  }

  if (isType(action, UpdateConfirmData)) {
    return {
      ...state,
      steps: {
        ...state.steps,
        [ShiftPostStep.CONFIRM]: {
          ...state.steps[ShiftPostStep.CONFIRM],
          data: {
            ...state.steps[ShiftPostStep.CONFIRM].data,
            ...action.payload,
          },
        },
      },
    }
  }

  // Step confirmation actions
  if (isType(action, ConfirmStep)) {
    const { step } = action.payload
    return {
      ...state,
      steps: {
        ...state.steps,
        [step]: {
          ...state.steps[step],
          confirmed: true,
        },
      },
    }
  }

  if (isType(action, ResetShiftPostV2)) {
    return initialState
  }

  if (isType(action, SetShiftPostV2Loading)) {
    return {
      ...state,
      isLoading: action.payload,
    }
  }

  if (isType(action, SetShiftPostV2Error)) {
    return {
      ...state,
      error: action.payload,
    }
  }

  if (isType(action, SetShiftPostV2InitialValues)) {
    return {
      ...state,
      initialValues: action.payload,
    }
  }

  if (isType(action, SetShiftPostV2CopyEditInitialValues)) {
    return {
      ...state,
      copyEditInitialValues: action.payload,
    }
  }

  if (isType(action, SetShiftPostV2CopyEditStatusForRequest)) {
    return {
      ...state,
      copyEditStatusForRequest: action.payload,
    }
  }

  if (isType(action, AddBadgesToEditInitialValues)) {
    return {
      ...state,
      copyEditInitialValues: state.copyEditInitialValues
        ? {
            ...state.copyEditInitialValues,
            badges: action.payload,
          }
        : null,
    }
  }

  if (isType(action, AddAutoAcceptCertificateIdsToEditInitialValues)) {
    return {
      ...state,
      copyEditInitialValues: state.copyEditInitialValues
        ? {
            ...state.copyEditInitialValues,
            auto_accept_certificate_ids: action.payload,
          }
        : null,
    }
  }

  // Rosters actions
  if (isType(action, RostersFetching)) {
    return {
      ...state,
      rosterData: {
        ...state.rosterData,
        isFetching: action.payload,
      },
    }
  }

  if (isType(action, GetRosters)) {
    return {
      ...state,
      rosterData: {
        ...state.rosterData,
        data: action.payload.data,
      },
    }
  }

  if (isType(action, GetRostersErrors)) {
    return {
      ...state,
      rosterData: {
        ...state.rosterData,
        error: action.payload,
      },
    }
  }

  if (isType(action, MoveRoster)) {
    const {
      payload: { id, direction },
    } = action
    const { data } = state.rosterData
    const { selectedOption, selectedRosters } = state.steps[
      ShiftPostStep.ACCEPTANCE_CRITERIA
    ].data

    if (selectedOption === ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS) {
      const formattedArray = moveItem<IRoster>(selectedRosters, id, direction)

      return {
        ...state,
        steps: {
          ...state.steps,
          [ShiftPostStep.ACCEPTANCE_CRITERIA]: {
            ...state.steps[ShiftPostStep.ACCEPTANCE_CRITERIA],
            data: {
              ...state.steps[ShiftPostStep.ACCEPTANCE_CRITERIA].data,
              selectedRosters: formattedArray,
            },
          },
        },
      }
    }

    const formattedArray = moveItem<IRoster>(data, id, direction)

    return {
      ...state,
      rosterData: {
        ...state.rosterData,
        data: formattedArray,
      },
    }
  }

  if (isType(action, ReorderRosters)) {
    const {
      payload: { oldIndex, newIndex },
    } = action
    const { data } = state.rosterData
    const { selectedOption, selectedRosters } = state.steps[
      ShiftPostStep.ACCEPTANCE_CRITERIA
    ].data
    if (selectedOption === ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS) {
      const formattedArray = reorderArray<IRoster>(
        selectedRosters,
        oldIndex,
        newIndex,
      )

      return {
        ...state,
        steps: {
          ...state.steps,
          [ShiftPostStep.ACCEPTANCE_CRITERIA]: {
            ...state.steps[ShiftPostStep.ACCEPTANCE_CRITERIA],
            data: {
              ...state.steps[ShiftPostStep.ACCEPTANCE_CRITERIA].data,
              selectedRosters: formattedArray,
            },
          },
        },
      }
    }

    const formattedArray = reorderArray<IRoster>(data, oldIndex, newIndex)

    return {
      ...state,
      rosterData: {
        ...state.rosterData,
        data: formattedArray,
      },
    }
  }

  if (isType(action, ReorderRostersByInitialIds)) {
    const {
      payload: { rosterIds },
    } = action
    const { data } = state.rosterData

    // Create a Map for rosters lookups
    const rosterMap = new Map(data.map(roster => [roster.id, roster]))

    // Get rosters in the specified order
    const orderedRosters = (rosterIds ?? [])
      .map(rosterId => rosterMap.get(rosterId))
      .filter(Boolean) as IRoster[]

    const availableRosters: IRoster[] = []
    const unavailableRosters: IRoster[] = []

    orderedRosters.forEach(roster => {
      if (roster.is_available_for_invite === false) {
        unavailableRosters.push(roster)
      } else {
        availableRosters.push(roster)
      }
    })

    const sortedSelectedRosters = [...availableRosters, ...unavailableRosters]

    // Get remaining rosters (those not in rosterIds)
    const remainingRosters = data.filter(
      roster => !rosterIds.includes(roster.id),
    )

    const sortedRosters = [...sortedSelectedRosters, ...remainingRosters]

    return {
      ...state,
      rosterData: {
        ...state.rosterData,
        data: sortedRosters,
      },
    }
  }

  // Invites History actions
  if (isType(action, InvitesHistoryFetching)) {
    return {
      ...state,
      invitesHistoryData: {
        ...state.invitesHistoryData,
        isFetching: action.payload,
      },
    }
  }

  if (isType(action, GetInvitesHistory)) {
    return {
      ...state,
      invitesHistoryData: {
        ...state.invitesHistoryData,
        data: action.payload.data,
      },
    }
  }

  if (isType(action, GetInvitesHistoryErrors)) {
    return {
      ...state,
      invitesHistoryData: {
        ...state.invitesHistoryData,
        error: action.payload,
      },
    }
  }
  return state
}
